/**
 * PKCE (Proof Key for Code Exchange) Utilities
 *
 * This module provides functions for generating PKCE code verifiers and challenges
 * according to the OAuth 2.0 specification (RFC 7636).
 *
 * PKCE is an extension to the Authorization Code flow to prevent authorization code interception
 * attacks where a malicious client can intercept the authorization code and exchange it for a token.
 *
 * @see https://datatracker.ietf.org/doc/html/rfc7636
 */

/**
 * Generates a random string suitable for use as a PKCE code verifier.
 * According to the spec, the code verifier should be a random string of 43-128 characters
 * from the unreserved URL characters.
 *
 * @param length The length of the code verifier (default: 64)
 * @returns A random string of the specified length using allowed characters
 */
export function generateCodeVerifier(length = 64): string {
  // Use crypto API if available for secure random values
  const charset =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";

  // Generate random bytes
  const randomBytes = new Uint8Array(length);
  crypto.getRandomValues(randomBytes);

  // Map random bytes to allowed character set
  let verifier = "";
  for (let i = 0; i < length; i++) {
    verifier += charset[randomBytes[i] % charset.length];
  }

  return verifier;
}

/**
 * Generates a code challenge from a code verifier using the S256 method.
 *
 * The S256 method consists of:
 * 1. SHA256 hash of the code verifier
 * 2. Base64URL encoding of the hash
 *
 * @param codeVerifier The code verifier to hash
 * @returns The code challenge as a base64url encoded string
 */
export async function generateCodeChallenge(
  codeVerifier: string
): Promise<string> {
  // Convert verifier to Uint8Array
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);

  // Generate SHA-256 hash
  const hash = await crypto.subtle.digest("SHA-256", data);

  // Convert hash to Base64URL format (base64 without padding and with URL-safe characters)
  return base64UrlEncode(hash);
}

/**
 * Encodes an ArrayBuffer to base64url format.
 * Base64URL is base64 with URL-safe characters and without padding.
 *
 * @param buffer The array buffer to encode
 * @returns A base64url encoded string
 */
function base64UrlEncode(buffer: ArrayBuffer): string {
  // Convert ArrayBuffer to base64
  const base64 = globalThis.btoa(
    String.fromCodePoint(...new Uint8Array(buffer))
  );

  // Convert to base64url format by replacing characters and removing padding
  return base64.replace(/\//g, "_").replace(/\+/g, "-").replace(/=/g, "");
}

/**
 * Stores PKCE values in local storage for use in the OAuth flow.
 *
 * @param codeVerifier The code verifier to store
 * @param state Optional state parameter to store
 */
export function storePkceParams(codeVerifier: string, state?: string): void {
  sessionStorage.setItem("pkce_code_verifier", codeVerifier);
  if (state) {
    sessionStorage.setItem("pkce_state", state);
  }
}

/**
 * Retrieves stored PKCE code verifier.
 *
 * @returns The stored code verifier or null if not found
 */
export function getStoredCodeVerifier(): string | null {
  return sessionStorage.getItem("pkce_code_verifier");
}

/**
 * Retrieves stored PKCE state parameter.
 *
 * @returns The stored state parameter or null if not found
 */
export function getStoredState(): string {
  return sessionStorage.getItem("pkce_state") || "";
}

/**
 * Clears stored PKCE parameters from storage.
 */
export function clearPkceParams(): void {
  sessionStorage.removeItem("pkce_code_verifier");
  sessionStorage.removeItem("pkce_state");
}

/**
 * Creates all necessary PKCE parameters for an OAuth 2.0 authorization request.
 *
 * @returns An object containing the code_verifier, code_challenge, code_challenge_method, and a random state
 */
export async function createPkceParameters(): Promise<{
  code_verifier: string;
  code_challenge: string;
  code_challenge_method: string;
  state: string;
}> {
  // Generate and store code verifier
  const codeVerifier = generateCodeVerifier();

  // Generate random state for CSRF protection
  const state = generateCodeVerifier(32);

  // Generate code challenge
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Store values for later use
  storePkceParams(codeVerifier, state);

  return {
    code_verifier: codeVerifier,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    state
  };
}
